import React, { useMemo } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigation } from '@react-navigation/native';
import { CHANGELOG_AUDIENCE_ADMIN, cardShowsCta } from '../utils/changelogService';

export function WhatsNewModal({ visible, release, onClose, onDismiss, preview = false }) {
  const { theme, isDarkMode } = useTheme();
  const navigation = useNavigation();
  const styles = useMemo(() => createStyles(theme, isDarkMode), [theme, isDarkMode]);

  if (!release) return null;

  const handleCta = (card) => {
    const route = String(card.ctaRoute || '').trim();
    if (!route) return;
    const params = card.ctaParams && typeof card.ctaParams === 'object' ? card.ctaParams : undefined;
    if (!preview) {
      navigation.navigate(route, params);
      onDismiss?.();
    }
  };

  const handleClose = () => {
    if (preview) {
      onClose?.();
      return;
    }
    onDismiss?.();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable style={styles.backdrop} onPress={handleClose}>
        <Pressable style={styles.panel} onPress={(e) => e?.stopPropagation?.()}>
          <View style={styles.header}>
            <View style={{ flex: 1, minWidth: 0 }}>
              {preview ? (
                <Text style={styles.previewBadge}>Preview</Text>
              ) : null}
              {release.audience === CHANGELOG_AUDIENCE_ADMIN ? (
                <Text style={styles.adminBadge}>Admin only</Text>
              ) : null}
              <Text style={styles.title}>{release.title}</Text>
              {release.subtitle ? <Text style={styles.subtitle}>{release.subtitle}</Text> : null}
              <Text style={styles.versionTag}>v{release.version}</Text>
            </View>
            <TouchableOpacity onPress={handleClose} hitSlop={12} style={styles.closeBtn}>
              <Ionicons name="close" size={22} color={theme.text.secondary} />
            </TouchableOpacity>
          </View>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {release.cards.map((card) => (
              <View key={card.id} style={styles.card}>
                <View style={styles.cardIconWrap}>
                  <Ionicons name={card.icon || 'sparkles-outline'} size={22} color={theme.brand.accent} />
                </View>
                <View style={styles.cardBody}>
                  <Text style={styles.cardTitle}>{card.title}</Text>
                  {card.body ? <Text style={styles.cardText}>{card.body}</Text> : null}
                  {cardShowsCta(card) ? (
                    <TouchableOpacity
                      style={styles.ctaBtn}
                      onPress={() => handleCta(card)}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.ctaBtnText}>{card.ctaLabel}</Text>
                      <Ionicons name="arrow-forward" size={14} color="#fff" />
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            ))}
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity style={styles.dismissBtn} onPress={handleClose} activeOpacity={0.88}>
              <Text style={styles.dismissBtnText}>{preview ? 'Close preview' : 'Got it'}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function createStyles(theme, isDarkMode) {
  const panelBg = isDarkMode ? '#1a1a1f' : '#ffffff';
  const cardBg = isDarkMode ? 'rgba(255,255,255,0.05)' : theme.background.secondary || '#f6f7f9';
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.62)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      zIndex: 100000,
      ...(Platform.OS === 'web' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 } : {}),
    },
    panel: {
      width: '100%',
      maxWidth: 520,
      maxHeight: Platform.OS === 'web' ? '85vh' : '88%',
      backgroundColor: panelBg,
      borderRadius: 18,
      borderWidth: 1,
      borderColor: theme.ui.border,
      overflow: 'hidden',
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 24px 80px rgba(0,0,0,0.45)' }
        : {}),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      paddingHorizontal: 22,
      paddingTop: 22,
      paddingBottom: 14,
      borderBottomWidth: 1,
      borderBottomColor: theme.ui.border,
    },
    previewBadge: {
      alignSelf: 'flex-start',
      fontSize: 10,
      fontWeight: '800',
      color: theme.brand.accent,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 6,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: `${theme.brand.accent}22`,
      overflow: 'hidden',
    },
    adminBadge: {
      alignSelf: 'flex-start',
      fontSize: 10,
      fontWeight: '800',
      color: '#d97706',
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 6,
      paddingHorizontal: 8,
      paddingVertical: 3,
      borderRadius: 999,
      backgroundColor: 'rgba(217, 119, 6, 0.15)',
      overflow: 'hidden',
    },
    title: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.text.primary,
      letterSpacing: -0.3,
    },
    subtitle: {
      fontSize: 13,
      color: theme.text.secondary,
      marginTop: 6,
      lineHeight: 19,
    },
    versionTag: {
      fontSize: 11,
      fontWeight: '700',
      color: theme.text.tertiary,
      marginTop: 8,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    closeBtn: {
      padding: 4,
      marginTop: 2,
    },
    scroll: { flex: 1 },
    scrollContent: {
      padding: 18,
      gap: 12,
    },
    card: {
      flexDirection: 'row',
      gap: 12,
      padding: 14,
      borderRadius: 14,
      backgroundColor: cardBg,
      borderWidth: 1,
      borderColor: theme.ui.border,
    },
    cardIconWrap: {
      width: 40,
      height: 40,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${theme.brand.accent}18`,
      flexShrink: 0,
    },
    cardBody: { flex: 1, minWidth: 0, gap: 6 },
    cardTitle: {
      fontSize: 15,
      fontWeight: '800',
      color: theme.text.primary,
    },
    cardText: {
      fontSize: 13,
      lineHeight: 19,
      color: theme.text.secondary,
    },
    ctaBtn: {
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 4,
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: 999,
      backgroundColor: theme.brand.accent,
    },
    ctaBtnText: {
      fontSize: 12,
      fontWeight: '800',
      color: '#fff',
    },
    footer: {
      paddingHorizontal: 18,
      paddingVertical: 14,
      borderTopWidth: 1,
      borderTopColor: theme.ui.border,
    },
    dismissBtn: {
      alignItems: 'center',
      paddingVertical: 12,
      borderRadius: 10,
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : theme.background.secondary,
    },
    dismissBtnText: {
      fontSize: 14,
      fontWeight: '800',
      color: theme.text.primary,
    },
  });
}
